import React, { useState } from 'react'
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  User,
  MessageCircle,
  ExternalLink,
  Calendar,
  GraduationCap
} from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulación de envío
    setTimeout(() => {
      alert('¡Mensaje enviado exitosamente! Nos pondremos en contacto contigo pronto.')
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      })
      setIsSubmitting(false)
    }, 1000)
  }

  const contactInfo = [
    {
      icon: MapPin,
      title: "Ubicación",
      details: [
        "Calle 98C # 1E-99",
        "Barranquilla, Colombia"
      ],
      color: "text-gardenia-red",
      link: "https://maps.google.com/?q=Calle+98C+1E-99+Barranquilla+Colombia"
    },
    {
      icon: Phone,
      title: "Teléfono",
      details: [
        "+57 "
      ],
      color: "text-gardenia-green",
      link: "tel:+57"
    },
    {
      icon: Mail,
      title: "Correo Electrónico",
      details: [
        "info@lasgardenias.edu.co"
      ],
      color: "text-gardenia-gold",
      link: "mailto:info@lasgardenias.edu.co"
    },
    {
      icon: Clock,
      title: "Horario de Atención",
      details: [
        "Lunes a Viernes",
        "6:30 AM - 6:00 PM"
      ],
      color: "text-gardenia-red"
    }
  ]

  const quickLinks = [
    {
      title: "Proceso de Admisión 2026",
      description: "Información completa sobre inscripciones",
      link: "/admisiones",
      icon: Calendar,
      color: "from-gardenia-red to-red-600"
    },
    {
      title: "Nuestros Programas",
      description: "Conoce la oferta educativa disponible",
      link: "/programas",
      icon: GraduationCap,
      color: "from-gardenia-green to-green-600"
    },
    {
      title: "Formulario Transición",
      description: "Inscripción para preescolar 2026",
      link: "https://forms.gle/QQ4JjHVqZJrdNFgo9",
      external: true,
      icon: ExternalLink,
      color: "from-gardenia-gold to-yellow-500"
    },
    {
      title: "Formulario 1° a 9°",
      description: "Inscripción para primaria y bachillerato",
      link: "https://forms.gle/TLS65F72QjcC6VgMA",
      external: true,
      icon: ExternalLink,
      color: "from-purple-500 to-purple-600"
    }
  ]

  const departments = [
    {
      name: "Rectoría",
      description: "Dirección general de la institución"
    },
    {
      name: "Coordinación Académica",
      description: "Seguimiento del proceso educativo"
    },
    {
      name: "Coordinación de Convivencia",
      description: "Bienestar estudiantil y disciplina"
    },
    {
      name: "Orientación Escolar",
      description: "Apoyo psicológico y vocacional"
    },
    {
      name: "Secretaría Académica",
      description: "Certificados y documentación"
    },
    {
      name: "Tesorería",
      description: "Pagos y asuntos financieros"
    }
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gardenia-red/10 to-gardenia-green/10">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green shadow-2xl mb-6">
                <MessageCircle className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gardenia-dark mb-6">
              Contacta con 
              <span className="text-gradient block mt-2">Nosotros</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
              Estamos aquí para resolver todas tus dudas sobre nuestra institución educativa. 
              Contáctanos y descubre todo lo que Las Gardenias puede ofrecer a tu familia.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Información de Contacto
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Múltiples formas de comunicarte con nosotros para brindarte la mejor atención.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon
              const content = (
                <Card key={index} className="text-center hover-lift" variant="bordered">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-red/10 to-gardenia-green/10 mb-4">
                      <IconComponent className={`h-8 w-8 ${info.color}`} />
                    </div>
                    <Card.Title className="text-lg">{info.title}</Card.Title>
                  </div>
                  <Card.Content>
                    {info.details.map((detail, idx) => (
                      <div key={idx} className="text-sm text-gray-600 mb-1">
                        {detail}
                      </div>
                    ))}
                  </Card.Content>
                </Card>
              )
              
              return info.link ? (
                <a key={index} href={info.link} target={info.link.startsWith('http') ? '_blank' : '_self'} rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}>
                  {content}
                </a>
              ) : content
            })}
          </div>
        </div>
      </section>

      {/* Contact Form and Quick Links */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gardenia-dark mb-6">
                Envíanos un Mensaje
              </h3>
              <p className="text-gray-600 mb-8">
                Completa el formulario y nos pondremos en contacto contigo lo antes posible.
              </p>

              <Card padding="lg">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gardenia-dark mb-2">
                        Nombre Completo *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gardenia-red focus:border-transparent transition-all duration-300"
                          placeholder="Tu nombre completo"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gardenia-dark mb-2">
                        Teléfono
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gardenia-red focus:border-transparent transition-all duration-300"
                          placeholder="Tu número de teléfono"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gardenia-dark mb-2">
                      Correo Electrónico *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gardenia-red focus:border-transparent transition-all duration-300"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gardenia-dark mb-2">
                      Asunto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gardenia-red focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="admisiones">Información sobre Admisiones</option>
                      <option value="programas">Consulta sobre Programas</option>
                      <option value="documentos">Solicitud de Documentos</option>
                      <option value="general">Información General</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gardenia-dark mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gardenia-red focus:border-transparent transition-all duration-300 resize-vertical"
                      placeholder="Escribe tu mensaje aquí..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          <span>Enviar Mensaje</span>
                        </>
                      )}
                    </span>
                  </Button>
                </form>
              </Card>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gardenia-dark mb-6">
                Enlaces Rápidos
              </h3>
              <p className="text-gray-600 mb-8">
                Accede directamente a la información más solicitada.
              </p>

              <div className="space-y-4 mb-8">
                {quickLinks.map((link, index) => {
                  const IconComponent = link.icon
                  return (
                    <Card key={index} className="hover-lift" padding="sm">
                      <div className="flex items-center space-x-4">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${link.color} flex-shrink-0`}>
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-semibold text-gardenia-dark mb-1">
                            {link.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {link.description}
                          </p>
                        </div>
                        <Button
                          to={link.external ? undefined : link.link}
                          href={link.external ? link.link : undefined}
                          external={link.external}
                          variant="outline"
                          size="sm"
                        >
                          {link.external ? 'Ir' : 'Ver'}
                        </Button>
                      </div>
                    </Card>
                  )
                })}
              </div>

              {/* Departments */}
              <Card padding="lg" variant="bordered">
                <Card.Title className="text-xl text-gardenia-red mb-4">
                  Dependencias
                </Card.Title>
                <div className="space-y-3">
                  {departments.map((dept, index) => (
                    <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                      <h5 className="font-semibold text-gardenia-dark text-sm">
                        {dept.name}
                      </h5>
                      <p className="text-xs text-gray-600">
                        {dept.description}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-gardenia-red to-gardenia-green text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Listo para Unirte a Nuestra Familia Educativa?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Da el primer paso hacia una educación de calidad. Estamos aquí para 
            acompañarte en todo el proceso y resolver todas tus inquietudes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/admisiones" variant="gold" size="lg">
              Proceso de Admisión
            </Button>
            <Button to="/nosotros" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gardenia-red">
              Conoce Más Sobre Nosotros
            </Button>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-gray-100">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gardenia-dark mb-4">
              Nuestra Ubicación
            </h3>
            <p className="text-gray-600">
              Calle 98C # 1E-99, Barranquilla, Colombia
            </p>
          </div>
          
          <Card className="overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3917.360846519495!2d-74.83219462427085!3d10.936091056226937!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8ef5d3d2239bc27d%3A0xbedb05b71d7f6e50!2sIED%20LAS%20GARDENIAS!5e0!3m2!1ses!2sus!4v1759202689328!5m2!1ses!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación Las Gardenias IED"
            ></iframe>
          </Card>
        </div>
      </section>
    </div>
  )
}

export default Contact